
/**
 * Utility functions for handling redirects and caching
 */

// In-memory cache to avoid unnecessary redirect checks
interface RedirectCacheEntry {
  destination: string | null;
  timestamp: number;
  statusCode?: number;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
const redirectCache: Record<string, RedirectCacheEntry> = {};

// Stats tracking
let cacheHits = 0;
let cacheMisses = 0;
let cacheRequests = 0;

/**
 * Normalize a path for consistent redirect handling
 */
export const normalizePath = (path: string): string => {
  if (!path) return '/';
  
  // Remove query params and hash
  let normalizedPath = path.split('?')[0].split('#')[0];
  
  // Ensure path starts with /
  if (!normalizedPath.startsWith('/')) {
    normalizedPath = '/' + normalizedPath;
  }
  
  // Remove trailing slash (except for root path)
  if (normalizedPath !== '/' && normalizedPath.endsWith('/')) {
    normalizedPath = normalizedPath.slice(0, -1);
  }
  
  return normalizedPath;
};

/**
 * Get a cached redirect if one exists
 * @param path The path to check for
 * @returns The cached redirect destination or undefined if not in cache
 */
export const getCachedRedirect = (path: string): string | null | undefined => {
  const normalized = normalizePath(path);
  cacheRequests++;
  
  const cached = redirectCache[normalized];
  
  if (!cached) {
    cacheMisses++;
    return undefined;
  }
  
  // Check if cache entry is expired
  if (Date.now() - cached.timestamp > CACHE_DURATION) {
    delete redirectCache[normalized]; // Remove expired entry
    cacheMisses++;
    return undefined;
  }
  
  cacheHits++;
  return cached.destination;
};

/**
 * Cache a redirect for future use
 * @param path The path to cache
 * @param destination The destination path or null if no redirect
 * @param statusCode Optional HTTP status code
 */
export const cacheRedirect = (path: string, destination: string | null | undefined, statusCode?: number): void => {
  const normalized = normalizePath(path);
  
  // If destination is undefined, it means we want to clear this entry
  if (destination === undefined) {
    delete redirectCache[normalized];
    return;
  }
  
  redirectCache[normalized] = {
    destination,
    timestamp: Date.now(),
    statusCode
  };
};

/**
 * Clear the entire redirect cache
 */
export const clearRedirectCache = (): void => {
  Object.keys(redirectCache).forEach(key => {
    delete redirectCache[key];
  });
  console.log('Redirect cache cleared');
};

/**
 * Clear specific path patterns from the redirect cache
 * @param pattern A string or RegExp pattern to match against cache keys
 */
export const clearRedirectCacheByPattern = (pattern: string | RegExp): void => {
  const keys = Object.keys(redirectCache);
  const matchedKeys = pattern instanceof RegExp
    ? keys.filter(key => pattern.test(key))
    : keys.filter(key => key.includes(pattern));
  
  matchedKeys.forEach(key => {
    delete redirectCache[key];
  });
  
  if (matchedKeys.length > 0) {
    console.log(`Cleared ${matchedKeys.length} entries from redirect cache matching: ${pattern}`);
  }
};

/**
 * Get cache statistics for monitoring and debugging
 * @returns Object containing cache stats
 */
export const getCacheStats = (): { size: number, hits: number, misses: number, cacheHitRate: number } => {
  const size = Object.keys(redirectCache).length;
  const cacheHitRate = cacheRequests > 0 ? (cacheHits / cacheRequests) * 100 : 0;
  
  return {
    size,
    hits: cacheHits,
    misses: cacheMisses,
    cacheHitRate
  };
};

/**
 * Get a canonical URL for a given path
 * @param path The path to get canonical URL for
 * @param baseUrl Optional base URL (defaults to edgefi.com)
 * @returns The canonical URL
 */
export const getCanonicalUrl = (path: string, baseUrl: string = 'https://edgefi.com'): string => {
  const normalized = normalizePath(path);
  
  // Check if this path has a redirect
  const cachedDestination = getCachedRedirect(normalized);
  
  // If we have a redirect destination, use that for the canonical URL
  const finalPath = cachedDestination || normalized;
  
  // Ensure baseUrl doesn't end with a slash
  const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  
  return `${cleanBaseUrl}${finalPath}`;
};
